import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  useGetCashReceiptSummaryMutation,
  useStoreCashReceiptMutation,
  useUpdateCashReceiptMutation,
  useReprintCashReceiptMutation,
} from "../../../../store/queries/Delivery";
import { useGetOrdersBasicDataQuery } from "../../../../store/queries/Orders/index.js";
import { updateConfig } from "../../../../store/slices/Delivery/invoicesSlice";

const useCashReceiptForm = ({
  onClose,
  onPrintReady,
  mode = "create",
  editData = null,
}) => {
  const isEdit = mode === "edit";
  const dispatch = useDispatch();
  const { selectedInvoiceIds, summaryData } = useSelector(
    (state) => state.deliveryInvoices,
  );

  const { data: basicData = {}, isLoading: isBasicDataLoading } =
    useGetOrdersBasicDataQuery();

  const [fetchSummary, { isLoading: isSummaryLoading }] =
    useGetCashReceiptSummaryMutation();
  const [storeCashReceipt, { isLoading: isStoring }] =
    useStoreCashReceiptMutation();
  const [updateCashReceipt, { isLoading: isUpdating }] =
    useUpdateCashReceiptMutation();
  const [reprintCashReceipt] = useReprintCashReceiptMutation();

  const isSubmitting = isStoring || isUpdating;

  useEffect(() => {
    if (isEdit) return;
    if (!selectedInvoiceIds?.length) return;
    fetchSummary({ invoices: selectedInvoiceIds })
      .unwrap()
      .then((res) => {
        dispatch(
          updateConfig((state) => {
            state.summaryData = res?.data ?? res;
          }),
        );
      })
      .catch(() => {
        toast.error("Failed to load summary");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInvoiceIds, isEdit]);

  const shopOptions = useMemo(
    () => basicData?.data?.supplier_list ?? [],
    [basicData],
  );

  const paymentOptions = useMemo(
    () => basicData?.data?.payment_mode ?? [],
    [basicData],
  );

  const totalInvoiceAmount = isEdit
    ? Number(editData?.total_invoice_amount ?? 0)
    : Number(summaryData?.total_invoice_amount ?? 0);
  const advanceAmount = isEdit
    ? Number(editData?.total_advance_amount ?? 0)
    : Number(summaryData?.total_advance_amount ?? 0);
  const balanceAmount = isEdit
    ? Number(editData?.balance_amount ?? totalInvoiceAmount - advanceAmount)
    : Number(
        summaryData?.total_balance_amount ?? totalInvoiceAmount - advanceAmount,
      );
  const supplierId = isEdit
    ? editData?.supplier?.id ?? editData?.supplier_id ?? ""
    : summaryData?.supplier ?? "";

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      supplier_id: supplierId,
      payment_method: isEdit ? editData?.payment_method ?? "" : "",
      total_invoice_amount: totalInvoiceAmount,
      total_advance_amount: advanceAmount,
      balance_amount: balanceAmount,
      remark: isEdit ? editData?.remark ?? "" : "",
    },
    validate: (values) => {
      const errors = {};
      if (!values.supplier_id) errors.supplier_id = "Required";
      if (!values.payment_method) errors.payment_method = "Required";
      const advance = Number(values.total_advance_amount);
      if (Number.isNaN(advance) || advance < 0) {
        errors.total_advance_amount = "Enter a valid amount";
      } else if (advance > Number(values.total_invoice_amount)) {
        errors.total_advance_amount =
          "Advance cannot exceed total invoice amount";
      }
      if (!values.remark?.trim()) errors.remark = "Required";
      return errors;
    },
    onSubmit: async (values) => {
      try {
        let response;
        if (isEdit) {
          response = await updateCashReceipt({
            cash_receipt_id: editData?.cash_receipt_id ?? editData?._id,
            supplier_id: values.supplier_id,
            payment_method: values.payment_method,
            total_invoice_amount: Number(values.total_invoice_amount),
            total_advance_amount: Number(values.total_advance_amount),
            balance_amount: Number(values.balance_amount),
            remark: values.remark,
          }).unwrap();
        } else {
          response = await storeCashReceipt({
            invoices: selectedInvoiceIds,
            supplier_id: values.supplier_id,
            payment_method: values.payment_method,
            total_invoice_amount: Number(values.total_invoice_amount),
            total_advance_amount: Number(values.total_advance_amount),
            balance_amount: Number(values.balance_amount),
            remark: values.remark,
          }).unwrap();
        }

        if (response?.status_code === 200 || response?.success) {
          let printData = response?.data ?? response;
          if (isEdit && !printData?.receipt_number) {
            const reprintRes = await reprintCashReceipt({
              cash_receipt_id: editData?.cash_receipt_id ?? editData?._id,
            }).unwrap();
            printData = reprintRes?.data ?? reprintRes;
          }
          dispatch(
            updateConfig((state) => {
              state.receiptPrintData = printData;
            }),
          );
          toast.success(
            response?.message ||
              (isEdit ? "Cash receipt updated" : "Cash receipt created"),
          );
          onClose?.();
          onPrintReady?.();
        } else {
          toast.error(
            response?.message ||
              (isEdit
                ? "Failed to update cash receipt"
                : "Failed to create cash receipt"),
          );
        }
      } catch (err) {
        toast.error(
          err?.data?.message ||
            (isEdit
              ? "Failed to update cash receipt"
              : "Failed to create cash receipt"),
        );
      }
    },
  });

  const advance = Number(formik.values.total_advance_amount) || 0;
  const invoiceTotal = Number(formik.values.total_invoice_amount) || 0;
  const computedBalance = invoiceTotal - advance;

  useEffect(() => {
    if (formik.values.balance_amount !== computedBalance) {
      formik.setFieldValue("balance_amount", computedBalance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedBalance]);

  const getFieldError = (name) =>
    formik.touched[name] && formik.errors[name] ? formik.errors[name] : "";

  return {
    formik,
    shopOptions,
    paymentOptions,
    isSummaryLoading,
    isBasicDataLoading,
    isSubmitting,
    getFieldError,
  };
};

export default useCashReceiptForm;
